import { useState, useEffect, useCallback, useRef } from "react";
import type { PaginatedResult } from "@/lib/api";
import type { PaginationState, SortDirection } from "@/components/ui/data-table/types";

interface UseDataTableOptions<TData, TFilters extends Record<string, unknown>> {
  /** Async function that fetches a page of data */
  fetcher: (
    params: { page: number; pageSize: number; search?: string; sortBy?: string; sortOrder?: "asc" | "desc" } & TFilters
  ) => Promise<{ success: boolean; data?: PaginatedResult<TData> }>;

  /** Items per page @default 25 */
  pageSize?: number;

  /** Initial filter values */
  initialFilters: TFilters;

  /** Debounce delay for search in ms @default 300 */
  searchDebounceMs?: number;

  /**
   * Map DataTable column IDs to backend sortBy keys.
   * Falls back to the column ID when no mapping exists.
   */
  sortKeyMap?: Record<string, string>;
}

interface UseDataTableReturn<TData, TFilters> {
  /** Current page data */
  data: TData[];

  /** Loading state */
  isLoading: boolean;

  /** Current search value */
  search: string;

  /** Update search value */
  setSearch: (value: string) => void;

  /** Current filter values */
  filters: TFilters;

  /** Update a single filter value */
  setFilter: <K extends keyof TFilters>(key: K, value: TFilters[K]) => void;

  /** Pagination state (ready to pass to DataTable) */
  pagination: PaginationState;

  /** Navigate to a specific page */
  setPage: (page: number) => void;

  /** Change page size (resets to page 1) */
  setPageSize: (size: number) => void;

  /** Manually trigger a data refresh */
  refresh: () => void;

  /** Current sort column ID (DataTable column, not API key) */
  sortColumnId: string | null;

  /** Current sort direction */
  sortDirection: SortDirection;

  /** Handle sort change from DataTable. Pass null direction to clear. */
  handleSort: (columnId: string, direction: SortDirection | null) => void;

  /** Reset search, filters and sort to initial values */
  resetFilters: () => void;
}

export function useDataTable<
  TData,
  TFilters extends Record<string, unknown> = Record<string, never>,
>(
  options: UseDataTableOptions<TData, TFilters>
): UseDataTableReturn<TData, TFilters> {
  const {
    fetcher,
    pageSize: initialPageSize = 25,
    initialFilters,
    searchDebounceMs = 300,
    sortKeyMap,
  } = options;

  const [data, setData] = useState<TData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState<TFilters>(initialFilters);
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    pageSize: initialPageSize,
    totalItems: 0,
    totalPages: 0,
  });

  // Sort state (column ID as displayed in DataTable)
  const [sortColumnId, setSortColumnId] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // Ref to track the latest fetch to prevent stale responses
  const fetchIdRef = useRef(0);

  // Stable ref for fetcher to avoid infinite re-render loops
  // (consumers pass inline arrow functions that change every render)
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Stable ref for sortKeyMap
  const sortKeyMapRef = useRef(sortKeyMap);
  sortKeyMapRef.current = sortKeyMap;

  // Stable ref for initialFilters
  const initialFiltersRef = useRef(initialFilters);
  initialFiltersRef.current = initialFilters;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, searchDebounceMs);
    return () => clearTimeout(timer);
  }, [search, searchDebounceMs]);

  // Reset page to 1 when search, filters, sort or page size change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filters, sortColumnId, sortDirection, pageSize]);

  // Fetch data
  const fetchData = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);

    try {
      // Map DataTable column ID to backend sortBy key
      const mappedSortBy = sortColumnId
        ? (sortKeyMapRef.current?.[sortColumnId] || sortColumnId)
        : undefined;

      const params = {
        page,
        pageSize,
        search: debouncedSearch || undefined,
        sortBy: mappedSortBy,
        sortOrder: sortColumnId ? sortDirection : undefined,
        ...filters,
      } as { page: number; pageSize: number; search?: string; sortBy?: string; sortOrder?: "asc" | "desc" } & TFilters;

      const response = await fetcherRef.current(params);

      // Ignore stale responses
      if (fetchId !== fetchIdRef.current) return;

      if (response.success && response.data) {
        setData(response.data.items);
        setPagination({
          page: response.data.page,
          pageSize: response.data.pageSize,
          totalItems: response.data.totalItems,
          totalPages: response.data.totalPages,
        });
      }
    } catch {
      // Ignore stale errors
      if (fetchId !== fetchIdRef.current) return;
      console.error("DataTable fetch failed");
    } finally {
      if (fetchId === fetchIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [page, pageSize, debouncedSearch, filters, sortColumnId, sortDirection]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const setFilter = useCallback(
    <K extends keyof TFilters>(key: K, value: TFilters[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const handleSort = useCallback(
    (columnId: string, direction: SortDirection | null) => {
      if (direction === null) {
        setSortColumnId(null);
        setSortDirection("asc");
      } else {
        setSortColumnId(columnId);
        setSortDirection(direction);
      }
    },
    []
  );

  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
  }, []);

  const resetFilters = useCallback(() => {
    setSearch("");
    setFilters(initialFiltersRef.current);
    setSortColumnId(null);
    setSortDirection("asc");
  }, []);

  return {
    data,
    isLoading,
    search,
    setSearch,
    filters,
    setFilter,
    pagination,
    setPage,
    setPageSize,
    refresh: fetchData,
    sortColumnId,
    sortDirection,
    handleSort,
    resetFilters,
  };
}
